import { useRef, useState, type CSSProperties, type FormEvent } from "react";
import {
  getPurposeOptionsForSiteTypes,
  type AnswerOptionGroup,
} from "../../shared/inquiry-flow";
import { SendIcon } from "./Icons";

interface ComposerProps {
  disabled: boolean;
  options: string[];
  optionGroups?: readonly AnswerOptionGroup[];
  placeholder: string;
  onSend: (answer: {
    content: string;
    freeText: string;
    selectedOptions: string[];
    selectedOptionsByGroup?: Record<string, string[]>;
  }) => Promise<void>;
}

function getOptionGroups(
  options: string[],
  optionGroups: readonly AnswerOptionGroup[] | undefined,
) {
  if (optionGroups?.length) return optionGroups;
  if (!options.length) return [];
  return [{ id: "default", label: "選択して回答", options }];
}

export function Composer({
  disabled,
  options,
  optionGroups,
  placeholder,
  onSend,
}: ComposerProps) {
  const [value, setValue] = useState("");
  const [selectedOptionsByGroup, setSelectedOptionsByGroup] = useState<
    Record<string, string[]>
  >({});
  const [activeGroupIndex, setActiveGroupIndex] = useState(0);
  const autoValueRef = useRef("");
  const baseGroups = getOptionGroups(options, optionGroups);
  const selectedSiteTypes = selectedOptionsByGroup["site-type"] ?? [];
  const groups = baseGroups.map((group) =>
    group.id === "purpose"
      ? { ...group, options: getPurposeOptionsForSiteTypes(selectedSiteTypes) }
      : group,
  );
  const isGrouped = Boolean(optionGroups?.length);
  // On mobile, every multi-part question shares one answer panel and advances
  // group by group. Single-group follow-up questions remain unchanged.
  const isStagedQuestion = groups.length > 1;
  const selectedOptions = groups.flatMap(
    (group) => selectedOptionsByGroup[group.id] ?? [],
  );
  const answerFillStyle = {
    "--answer-fill": `${Math.round((value.length / 500) * 100)}%`,
  } as CSSProperties;

  function getSelectionText(nextGroups: typeof groups, nextState: Record<string, string[]>) {
    return nextGroups
      .map((group) => {
        const groupOptions = nextState[group.id] ?? [];
        return groupOptions.length ? groupOptions.join("、") : "";
      })
      .filter(Boolean)
      .join("。")
      .slice(0, 500);
  }

  function toggleOption(groupId: string, option: string) {
    setSelectedOptionsByGroup((current) => {
      const groupOptions = current[groupId] ?? [];
      const nextOptions = groupOptions.includes(option)
        ? groupOptions.filter((item) => item !== option)
        : [...groupOptions, option];
      const nextState = { ...current, [groupId]: nextOptions };
      if (groupId === "site-type") {
        const allowedPurposeOptions = new Set(
          getPurposeOptionsForSiteTypes(nextOptions),
        );
        nextState.purpose = (current.purpose ?? []).filter((item) =>
          allowedPurposeOptions.has(item),
        );
      }
      const nextGroups = baseGroups.map((group) =>
        group.id === "purpose"
          ? { ...group, options: getPurposeOptionsForSiteTypes(nextState["site-type"] ?? []) }
          : group,
      );
      const nextSelectionText = getSelectionText(nextGroups, nextState);
      setValue((currentValue) => {
        const previousAutoValue = autoValueRef.current;
        const manualText = previousAutoValue && currentValue.startsWith(previousAutoValue)
          ? currentValue.slice(previousAutoValue.length).replace(/^。?\s*/, "")
          : currentValue.trim();
        const nextValue = [nextSelectionText, manualText].filter(Boolean).join("。").slice(0, 500);
        autoValueRef.current = nextSelectionText;
        return nextValue;
      });
      return nextState;
    });
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const trimmedValue = value.trim();
    const autoValue = autoValueRef.current.trim();
    const freeText = autoValue && trimmedValue.startsWith(autoValue)
      ? trimmedValue.slice(autoValue.length).replace(/^。?\s*/, "").trim()
      : trimmedValue;
    const selectedText =
      groups.length > 1
        ? groups
            .map((group) => {
              const groupOptions = selectedOptionsByGroup[group.id] ?? [];
              return groupOptions.length
                ? `${group.label}：${groupOptions.join("、")}`
                : "";
            })
            .filter(Boolean)
            .join("。")
        : selectedOptions.join("、");
    const message = [selectedText, freeText].filter(Boolean).join("。");
    if (!message || disabled) return;
    setValue("");
    autoValueRef.current = "";
    setSelectedOptionsByGroup({});
    await onSend({
      content: message,
      freeText,
      selectedOptions,
      ...(optionGroups?.length ? { selectedOptionsByGroup } : {}),
    });
  }

  return (
    <form className="composer" onSubmit={handleSubmit}>
      {groups.length > 0 ? (
        <div
          className={`answer-groups${isGrouped ? " is-grouped" : ""}${groups.length > 1 ? " is-split" : ""}${isStagedQuestion ? " is-staged" : ""}`}
        >
          {groups.map((group, index) => (
            <fieldset
              className={`answer-options${isStagedQuestion ? " is-staged-option" : ""}${isStagedQuestion && index === activeGroupIndex ? " is-active" : ""}`}
              disabled={disabled}
              key={group.id}
            >
              <legend className={group.id === "purpose" ? "sr-only" : undefined}>
                <span className="answer-group-title">{group.label}</span>
              </legend>
              <div className="answer-option-list">
                {group.options.map((option) => {
                  const selected = (selectedOptionsByGroup[group.id] ?? []).includes(option);
                  return (
                    <label
                      className={`answer-option${selected ? " selected" : ""}`}
                      key={option}
                    >
                      <input
                        type="checkbox"
                        name={`answer-options-${group.id}`}
                        value={option}
                        checked={selected}
                        onChange={() => toggleOption(group.id, option)}
                      />
                      <span>{option}</span>
                    </label>
                  );
                })}
              </div>
            </fieldset>
          ))}
          {isStagedQuestion ? (
            <div className="stage-navigation" aria-label="選択肢の切り替え">
              <button
                type="button"
                className="stage-navigation-button"
                disabled={activeGroupIndex === 0}
                onClick={() => setActiveGroupIndex((index) => Math.max(0, index - 1))}
              >
                戻る
              </button>
              <span aria-live="polite">{activeGroupIndex + 1} / {groups.length}</span>
              <button
                type="button"
                className="stage-navigation-button"
                disabled={activeGroupIndex >= groups.length - 1}
                onClick={() => setActiveGroupIndex((index) => Math.min(groups.length - 1, index + 1))}
              >
                次へ
              </button>
            </div>
          ) : null}
        </div>
      ) : null}
      <label className="sr-only" htmlFor="answer">
        自由入力で回答
      </label>
      <p className="free-answer-label">自由入力で補足 <span>任意</span></p>
      <div className="answer-input-wrap">
        <textarea
          id="answer"
          maxLength={500}
          placeholder={placeholder || "例：詳しい状況やご希望を入力してください"}
          value={value}
          onChange={(event) => setValue(event.target.value.slice(0, 500))}
          disabled={disabled}
          rows={3}
          style={answerFillStyle}
        />
        <button
          className="send-answer"
          type="submit"
          aria-label={disabled ? "回答を送信中" : "回答を送信"}
          disabled={disabled || (!value.trim() && selectedOptions.length === 0)}
        >
          <SendIcon />
          <span className="sr-only">{disabled ? "送信中" : "回答を送信"}</span>
        </button>
      </div>
    </form>
  );
}
